import { Button } from '@/components/Button';
import { FileInput } from '@/components/FileInput';
import { LineSeparator } from '@/components/LineSeparator';
import { Modal } from '@/components/Modal';
import { Switch } from '@/components/Switch';
import { Text } from '@/components/Text';
import { TextEditor } from '@/components/TextEditor';
import { TextInput } from '@/components/TextInput';
import { useSubscription } from '@/hooks/useSubscription';
import { testFollowUpMail } from '@/services/cardService';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import { handleError } from '@/utils/handleError';
import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FaCode } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

// Type pour la manipulation interne du composant
type FollowUpType = {
  id?: number;
  card_lead_capture_id?: number;
  subject: string;
  body: string;
  cc: string;
  bcc: string;
  delay: number;
  is_active: boolean;
};

// Type pour l'API - type exact attendu par l'API
type CardLeadCaptureFollowUpType = {
  id: number;
  card_lead_capture_id: number;
  subject: string;
  body: string;
  cc: string;
  bcc: string;
  delay: number;
  is_active: boolean;
};

export const UpdateCardFollowUpEmail = () => {
  const { t } = useTranslation();
  const { card, updateCard } = useCardStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [followUp, setFollowUp] = useState<FollowUpType | null>(null);
  const { hasActiveSubscription } = useSubscription(card?.user ?? null);
  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [variableTarget, setVariableTarget] = useState<'subject' | 'body'>(
    'subject',
  );
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const bodyQuillRef = useRef<ReactQuill>(null);

  const validationSchema = yup.object().shape({
    subject: yup.string().required(t('common:validation.required')),
    body: yup.string().required(t('common:validation.required')),
    cc: yup
      .string()
      .email(t('common:validation.email.format'))
      .nullable()
      .notRequired(),
    bcc: yup
      .string()
      .email(t('common:validation.email.format'))
      .nullable()
      .notRequired(),
    delay: yup
      .number()
      .min(0)
      .max(720)
      .required(t('common:validation.required')),
  });

  useEffect(() => {
    if (card?.lead_capture_follow_up) {
      const followUpData = card.lead_capture_follow_up;
      setFollowUp({
        id: followUpData.id,
        card_lead_capture_id: followUpData.card_lead_capture_id,
        subject: followUpData.subject || '',
        body: followUpData.body || '',
        cc: followUpData.cc || '',
        bcc: followUpData.bcc || '',
        delay: followUpData.delay || 0,
        is_active: followUpData.is_active || false,
      });
    } else if (card?.id) {
      setFollowUp({
        subject: "J'ai bien reçu vos informations de contact !",
        body: "Merci de m'avoir partagé vos informations de contact. Je vous recontacterai dans les plus brefs délais.",
        cc: '',
        bcc: '',
        delay: 0,
        is_active: false,
      });
    }
  }, [card]);

  const handleSwitchChange = async (checked: boolean) => {
    if (!followUp || !card) return;

    try {
      setLoading(true);

      const updatedFollowUp = {
        ...followUp,
        is_active: checked,
      };

      // Conversion en type CardLeadCaptureFollowUp pour l'API
      await updateCard(card.slug, {
        ...card,
        lead_capture_follow_up:
          updatedFollowUp as unknown as CardLeadCaptureFollowUpType,
      });

      setFollowUp(updatedFollowUp);
      toast.success(
        checked
          ? t('updateCard:follow-up.activated')
          : t('updateCard:follow-up.deactivated'),
      );
    } catch (error) {
      handleError(error, undefined, navigate);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: FollowUpType) => {
    if (!card) return;
    try {
      setFormLoading(true);
      await updateCard(card.slug, {
        ...card,
        lead_capture_follow_up: {
          ...followUp,
          ...values,
          subject: values.subject,
          body: values.body,
        } as CardLeadCaptureFollowUpType,
      });
      setFollowUp({ ...followUp, ...values });
      return true;
    } catch (error) {
      handleError(error, undefined, navigate);
      return false;
    } finally {
      setFormLoading(false);
    }
  };

  const handleTestSend = async (values: FollowUpType) => {
    if (!card?.id || !user?.email) return;
    try {
      const success = await handleSubmit(values);
      if (success) {
        await testFollowUpMail(card.id, user.email);
        toast.success('Mail de test envoyé !');
      }
    } catch (error) {
      handleError(error);
    }
  };

  if (!followUp) return null;

  return (
    <div className="space-y-6">
      {/* Activer/désactiver */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Text className="font-medium text-gray-900">
            {t('updateCard:follow-up.activate')}
          </Text>
          <Text className="text-sm text-gray-500">
            {t('updateCard:follow-up.activate-description')}
          </Text>
        </div>
        <Switch
          checked={followUp.is_active}
          onChange={handleSwitchChange}
          disabled={loading}
        />
      </div>

      <LineSeparator />

      <Formik
        enableReinitialize
        initialValues={{
          subject: followUp.subject,
          body: followUp.body,
          cc: followUp.cc,
          bcc: followUp.bcc,
          delay: followUp.delay,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const success = await handleSubmit(values as FollowUpType);
          if (success) {
            toast.success(t('updateCard:follow-up.saved'));
          }
          setSubmitting(false);
        }}
      >
        {({ errors, touched, setFieldValue, values }) => (
          <Form className="space-y-6">
            <Text className="font-medium text-gray-900">
              {t('updateCard:follow-up.email-settings')}
            </Text>

            <div className="space-y-4">
              <div>
                <div className="mb-2 flex items-center">
                  <label className="text-xs font-medium text-gray-600">
                    {t('updateCard:follow-up.subject')}
                    <span className="ml-1 text-danger">*</span>
                  </label>
                  <button
                    type="button"
                    className="ml-3 flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline"
                    onClick={() => {
                      setVariableTarget('subject');
                      setVariableModalOpen(true);
                    }}
                  >
                    <FaCode className="inline-block" />
                    {t('updateCard:follow-up.insert-variable')}
                  </button>
                </div>
                <TextInput
                  name="subject"
                  type="text"
                  placeholder={t('updateCard:follow-up.subject-placeholder')}
                  disabled={formLoading}
                  error={
                    !!touched.subject && !!errors.subject && errors.subject
                  }
                  helper={t('updateCard:follow-up.variables-helper')}
                  inputRef={subjectInputRef}
                />
              </div>
              <Modal
                isOpen={variableModalOpen}
                onClose={() => setVariableModalOpen(false)}
                title={t('updateCard:follow-up.variables-modal-title')}
              >
                <div className="space-y-2">
                  <Text className="text-sm text-gray-700">
                    {t('updateCard:follow-up.variables-modal-description')}
                  </Text>
                  <div>
                    <button
                      type="button"
                      className="rounded bg-gray-100 px-2 py-1 font-mono text-sm hover:bg-sky-100"
                      onClick={() => {
                        if (variableTarget === 'subject') {
                          const input = subjectInputRef.current;
                          if (input) {
                            const value = values.subject || '';
                            const variable = ' {{full_name}}';
                            const start = input.selectionStart || 0;
                            const end = input.selectionEnd || 0;
                            const newValue =
                              value.slice(0, start) +
                              variable +
                              value.slice(end);
                            setFieldValue('subject', newValue);
                            setVariableModalOpen(false);
                            setTimeout(() => {
                              input.focus();
                              input.setSelectionRange(
                                start + variable.length,
                                start + variable.length,
                              );
                            }, 0);
                          } else {
                            setFieldValue(
                              'subject',
                              (values.subject || '') + ' {{full_name}}',
                            );
                            setVariableModalOpen(false);
                          }
                        } else if (variableTarget === 'body') {
                          const quill = bodyQuillRef.current?.getEditor?.();
                          if (quill) {
                            const variable = ' {{full_name}}';
                            const range = quill.getSelection();
                            if (range) {
                              quill.insertText(range.index, variable);
                              quill.setSelection(
                                range.index + variable.length,
                                0,
                              );
                            } else {
                              quill.insertText(quill.getLength(), variable);
                            }
                            setVariableModalOpen(false);
                          }
                        }
                      }}
                    >
                      {'{{full_name}}'}
                    </button>
                    <span className="ml-2 text-xs text-gray-500">
                      {t('updateCard:follow-up.variables-modal-full-name')}
                    </span>
                  </div>
                </div>
              </Modal>

              <div>
                <div className="mb-2 flex items-center">
                  <label className="text-xs font-medium text-gray-600">
                    {t('updateCard:follow-up.body')}
                    <span className="ml-1 text-danger">*</span>
                  </label>
                  <button
                    type="button"
                    className="ml-3 flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline"
                    onClick={() => {
                      setVariableTarget('body');
                      setVariableModalOpen(true);
                    }}
                  >
                    <FaCode className="inline-block" />
                    {t('updateCard:follow-up.insert-variable')}
                  </button>
                </div>
                <TextEditor
                  isRequired
                  name="body"
                  disabled={formLoading}
                  placeholder={t('updateCard:follow-up.body-placeholder')}
                  helper={t('updateCard:follow-up.variables-helper')}
                  error={!!touched.body && !!errors.body && errors.body}
                  ref={bodyQuillRef}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextInput
                  label={t('updateCard:follow-up.cc')}
                  name="cc"
                  type="text"
                  placeholder={t('updateCard:follow-up.cc-placeholder')}
                  disabled={formLoading}
                  error={!!touched.cc && !!errors.cc && errors.cc}
                />

                <TextInput
                  label={t('updateCard:follow-up.bcc')}
                  name="bcc"
                  type="text"
                  placeholder={t('updateCard:follow-up.bcc-placeholder')}
                  disabled={formLoading}
                  error={!!touched.bcc && !!errors.bcc && errors.bcc}
                />

                <TextInput
                  label={t('updateCard:follow-up.delay')}
                  name="delay"
                  type="number"
                  placeholder="0"
                  disabled={formLoading}
                  helper={t('updateCard:follow-up.delay-info')}
                  min={0}
                  max={720}
                  isRequired
                  error={!!touched.delay && !!errors.delay && errors.delay}
                />
              </div>
            </div>

            {/* Pièces jointes */}
            {card?.id && (
              <div className="space-y-2">
                <Text className="font-medium text-gray-900">
                  {t('updateCard:follow-up.attachments-title')}
                </Text>
                <FileInput
                  name="followUpAttachments"
                  cardId={card.id}
                  label={t('updateCard:follow-up.attachments-label')}
                  helper={t('updateCard:follow-up.attachments-helper')}
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Button
                type="button"
                onClick={() => handleTestSend(values as FollowUpType)}
                disabled={formLoading || !hasActiveSubscription()}
                className="w-full bg-linko-light"
              >
                {t('updateCard:follow-up.test-send')}
              </Button>
              <Button
                type="submit"
                disabled={formLoading || !hasActiveSubscription()}
                className="w-full"
              >
                {formLoading ? t('common:saving') : t('common:save')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
